import { seededRandom } from "./cardTypes";
import { PROGRAM_NAME_OPTIONS } from "../../../types/configurationTypes";

export type TimeGranularity = "day" | "week" | "month";

export interface SubProgramDef {
  name: string;
  hasSMT: boolean;
  hasFATP: boolean;
}

export interface ShipmentData {
  program: string;
  subProgram: string;
  cmLocation: string;
  requestedQty: number;
  actualQty: number;
  gapQty: number;
  otdcHitRate: number;
  cumQty: number;
}

export interface PHICItem {
  program: string;
  alertType: string;
  status: "New" | "Open" | "Acknowledged" | "Closed";
  cmLocation: string;
  stage: string;
  date: string;
  description: string;
}

export const COLORS = [
  "#4E79A7", "#F28E2B", "#E15759", "#76B7B2", "#59A14F",
  "#EDC948", "#B07AA1", "#FF9DA7", "#9C755F", "#BAB0AC",
  "#A0CBE8", "#FFBE7D", "#8CD17D", "#B6992D", "#F1CE63",
  "#499894", "#86BCB6", "#D4A6C8", "#D7B5A6", "#79706E",
  "#AF7AA1", "#E15759", "#FF9D9A", "#86BCB6", "#D37295",
];

export const CAUSE_TYPES = ["Incoming Material", "Equipment / Fixture", "Process Related", "Operator Error", "Design Issue", "Software Bug", "Environmental"];

export const ALL_CM_LOCATIONS = ["Foxconn-Shenzhen", "Foxconn-Zhengzhou", "Pegatron-Shanghai", "Luxshare-Kunshan", "Wistron-Chengdu", "Compal-Chennai", "Quanta-Shanghai"];

export const getCMLocationsForProgram = (programIdx: number): string[] => {
  if (programIdx === 0) return ALL_CM_LOCATIONS.slice(0, 6);
  if (programIdx === 1) return ["Foxconn-Shenzhen", "Foxconn-Zhengzhou", "Pegatron-Shanghai"];
  if (programIdx === 2) return ["Foxconn-Zhengzhou", "Luxshare-Kunshan", "Wistron-Chengdu"];
  if (programIdx === 3) return ["Foxconn-Shenzhen", "Pegatron-Shanghai"];
  const count = 2 + Math.floor(seededRandom(programIdx * 77 + 33) * 3);
  const shuffled = [...ALL_CM_LOCATIONS];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(seededRandom(programIdx * 50 + i * 7) * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count);
};

export const getSubProgramDefs = (programIdx: number): SubProgramDef[] => {
  const map: Record<number, SubProgramDef[]> = {
    0: [{ name: "D63 Proto", hasSMT: true, hasFATP: true }, { name: "D63 Proto Upper", hasSMT: true, hasFATP: false }, { name: "D63 Proto Lower", hasSMT: false, hasFATP: true }],
    1: [{ name: "D63 EVT", hasSMT: true, hasFATP: true }],
    2: [{ name: "D63 DVT", hasSMT: true, hasFATP: true }, { name: "D63 DVT-A", hasSMT: true, hasFATP: false }],
    3: [{ name: "D63 PVT", hasSMT: true, hasFATP: true }],
    4: [{ name: "N85 MP", hasSMT: true, hasFATP: true }, { name: "N85 MP Upper", hasSMT: true, hasFATP: false }, { name: "N85 MP Lower", hasSMT: false, hasFATP: true }],
    5: [{ name: "N85 Ramp", hasSMT: true, hasFATP: true }],
    6: [{ name: "J510 Ramp", hasSMT: true, hasFATP: true }, { name: "J510 Ramp-A", hasSMT: true, hasFATP: true }, { name: "J510 Ramp-B", hasSMT: false, hasFATP: true }],
    7: [{ name: "J510 MP", hasSMT: true, hasFATP: true }],
  };
  return map[programIdx] || [{ name: PROGRAM_NAME_OPTIONS[programIdx]?.label || "Program", hasSMT: true, hasFATP: true }];
};

export const generateShipmentData = (programs: string[]): ShipmentData[] => {
  const rows: ShipmentData[] = [];
  programs.forEach((p, pIdx) => {
    const cmLocs = getCMLocationsForProgram(pIdx);
    const subDefs = getSubProgramDefs(pIdx);
    cmLocs.forEach((loc, lIdx) => {
      subDefs.forEach((subDef, sIdx) => {
        const s = pIdx * 200 + lIdx * 30 + sIdx * 11 + 7;
        const requested = 8000 + Math.floor(seededRandom(s + 1) * 12000);
        const actual = Math.floor(requested * (1.0 + seededRandom(s + 2) * 0.05));
        rows.push({ program: p, subProgram: subDef.name, cmLocation: loc, requestedQty: requested, actualQty: actual, gapQty: actual - requested, otdcHitRate: (actual / requested) * 100, cumQty: actual + Math.floor(seededRandom(s + 3) * 25000) + 10000 });
      });
    });
  });
  return rows;
};

export const generatePHICData = (programs: string[]): PHICItem[] => {
  const alerts = ["Output Hit Rate", "Machine Downtime", "Test FPY", "Assembly FPY"];
  const statuses: PHICItem["status"][] = ["New", "Open", "Acknowledged", "Closed"];
  const locations = ["GTK-Vietnam", "GTK-Shenzhen", "GTK-Weifang", "CNT-CN HY", "GTK-Fushan"];
  const stages = ["SMT", "FATP", "Assembly", "Testing"];
  const descriptions = [
    "Output below 95% target", "Downtime exceeded threshold", "FPY drop after fixture change",
    "Material shortage on line", "Screw torque out of spec", "Calibration drift detected",
    "Burn-in temperature anomaly", "Solder paste viscosity issue",
  ];
  const items: PHICItem[] = [];
  programs.forEach((p, pIdx) => {
    const numIssues = 4 + Math.floor(seededRandom(pIdx * 99) * 6);
    for (let i = 0; i < numIssues; i++) {
      const s = pIdx * 400 + i * 17;
      items.push({
        program: p, alertType: alerts[Math.floor(seededRandom(s + 1) * alerts.length)],
        status: statuses[Math.floor(seededRandom(s + 2) * statuses.length)],
        cmLocation: locations[Math.floor(seededRandom(s + 3) * locations.length)],
        stage: stages[Math.floor(seededRandom(s + 4) * stages.length)],
        date: `2026-05-${String(10 + Math.floor(seededRandom(s + 5) * 16)).padStart(2, "0")}`,
        description: descriptions[Math.floor(seededRandom(s + 6) * descriptions.length)],
      });
    }
  });
  return items;
};

export const generateTimeLabels = (granularity: TimeGranularity, selectedValue: string | null): string[] => {
  if (granularity === "month") {
    const baseMonth = selectedValue ? parseInt(selectedValue, 10) : 5;
    return Array.from({ length: 12 }, (_, i) => {
      const d = new Date(2026, baseMonth - 1 - 11 + i, 1);
      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    });
  }
  const baseWeek = selectedValue ? parseInt(selectedValue.replace("WK", ""), 10) : 22;
  return Array.from({ length: 12 }, (_, i) => {
    const w = baseWeek - 11 + i;
    if (w <= 0) return `2025-WK${String(52 + w).padStart(2, "0")}`;
    return `2026-WK${String(w).padStart(2, "0")}`;
  });
};
